import moment from 'moment';

import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import { DEFAULT_BAR_COLORS, DEFAULT_BAR_WIDTH } from './_constants';
import type {
  ICompareData,
  IDodResult,
  ISeriesConfig,
  IHorBarRenderListResult,
  IYAxisDateFormatByDateType,
} from './_type';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getName } from '@home/utils/common';

export type TStudioTrendPeriod = 'daily' | 'weekly' | 'monthly';

export interface IStudioTrendDateConfig {
  axisDateFormat: string;
  tableDateFormat: string;
  tooltipDateFormat: string;
}

interface IBuildHorBarParams {
  trendList: any[];
  seriesConfig: ISeriesConfig[];
  formatRule: string;
  xAxisFormat?: string;
  isEn: boolean;
  period: TStudioTrendPeriod;
  dateConfig: IStudioTrendDateConfig;
}

export function normalizeStudioTrendPeriod(raw: any): TStudioTrendPeriod {
  if (raw === 'weekly' || raw === 'week') {
    return 'weekly';
  }
  if (raw === 'monthly' || raw === 'month') {
    return 'monthly';
  }
  return 'daily';
}

const HOR_BAR_Y_AXIS_DEFAULTS: Record<TStudioTrendPeriod, string> = {
  daily: 'MM-DD',
  weekly: 'MM-DD',
  monthly: 'YYYY-MM',
};

/**
 * 解析 Y 轴日期格式：支持按粒度映射；纯 string 时月粒度不用 MM-DD，避免与日月粒度混用。
 */
export function resolveHorBarYAxisDateFormat(
  raw: string | IYAxisDateFormatByDateType | undefined,
  period: TStudioTrendPeriod,
): string {
  if (raw && typeof raw === 'object') {
    if (period === 'weekly') {
      return raw.weekly ?? raw.daily ?? HOR_BAR_Y_AXIS_DEFAULTS.weekly;
    }
    if (period === 'monthly') {
      return raw.monthly ?? raw.daily ?? HOR_BAR_Y_AXIS_DEFAULTS.monthly;
    }
    return raw.daily ?? HOR_BAR_Y_AXIS_DEFAULTS.daily;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    if (period === 'monthly') {
      return HOR_BAR_Y_AXIS_DEFAULTS.monthly;
    }
    return raw;
  }
  return HOR_BAR_Y_AXIS_DEFAULTS[period];
}

export function getStudioTrendDateConfig(
  period: TStudioTrendPeriod,
  opts: { axisDateFormat: string; },
): IStudioTrendDateConfig {
  const { axisDateFormat } = opts;
  if (period === 'monthly') {
    return {
      axisDateFormat: axisDateFormat || 'YYYY-MM',
      tableDateFormat: 'YYYY-MM',
      tooltipDateFormat: 'YYYY-MM',
    };
  }
  if (period === 'weekly') {
    return {
      axisDateFormat: axisDateFormat || 'MM-DD',
      tableDateFormat: 'YYYY-MM-DD',
      tooltipDateFormat: 'MM-DD',
    };
  }
  return {
    axisDateFormat: axisDateFormat || 'MM-DD',
    tableDateFormat: 'YYYY-MM-DD',
    tooltipDateFormat: 'MM-DD',
  };
}

const parseStudioDate = (raw: any) => {
  const text = String(raw ?? '').trim();
  if (/^\d{8}$/.test(text)) {
    return moment(
      `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`,
      'YYYY-MM-DD',
    );
  }
  return moment(text);
};

const formatStudioAxisDate = (raw: any, cfg: IStudioTrendDateConfig) => {
  const d = parseStudioDate(raw);
  if (!d.isValid()) {
    return String(raw ?? '');
  }
  return d.format(cfg.axisDateFormat);
};

const formatStudioTableDate = (raw: any, cfg: IStudioTrendDateConfig) => {
  const d = parseStudioDate(raw);
  if (!d.isValid()) {
    return String(raw ?? '');
  }
  return d.format(cfg.tableDateFormat);
};

const formatStudioTooltipDate = (
  raw: any,
  period: TStudioTrendPeriod,
  cfg: IStudioTrendDateConfig,
) => {
  const d = parseStudioDate(raw);
  if (!d.isValid()) {
    return String(raw ?? '');
  }

  if (period === 'weekly') {
    const mon = d.clone().startOf('isoWeek').format(cfg.tooltipDateFormat);
    const sun = d.clone().endOf('isoWeek').format(cfg.tooltipDateFormat);
    return `${mon} ~ ${sun}`;
  }

  return d.format(cfg.tooltipDateFormat);
};


export function buildHorBarRenderList(params: IBuildHorBarParams): IHorBarRenderListResult {
  const {
    trendList, seriesConfig, formatRule, xAxisFormat,
    isEn, period, dateConfig,
  } = params;
  const xAxisFormatRule = xAxisFormat || 'bmk';

  const sorted = [...trendList].sort((a, b) => {
    const da = moment(String(a?.date));
    const db = moment(String(b?.date));
    if (da.isBefore(db)) {
      return -1;
    }
    if (da.isAfter(db)) {
      return 1;
    }
    return 0;
  });

  const yAxisData = sorted.map(item => formatStudioAxisDate(item?.date, dateConfig));

  const series = seriesConfig.map((cfg, idx) => {
    const seriesName = getName(isEn, cfg.name, cfg.nameCn) || cfg.name;
    const data = sorted.map(item => {
      const v = item?.metrics?.[cfg.metricKey]?.value;
      return v !== '' && v !== null && v !== undefined ? parseFloat(v) : null;
    });

    return {
      name: seriesName,
      type: 'bar' as const,
      data,
      barWidth: DEFAULT_BAR_WIDTH,
      barCategoryGap: '10%',
      barGap: '35%',
      itemStyle: { color: cfg.color || DEFAULT_BAR_COLORS[idx] || DEFAULT_BAR_COLORS[0] },
      label: {
        show: true,
        position: 'right' as const,
        fontSize: 12,
        color: '#444444',
        formatter: (p: any) => {
          if (p.value === null || p.value === undefined) return '';
          return dataFormat({ value: p.value, formatRule: cfg.formatter || formatRule });
        },
      },
    };
  });

  const numericValues = series
    .flatMap(item => item.data)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const maxSeriesValue = Math.max(0, ...numericValues);
  const yAxisMax = maxSeriesValue > 0 ? Math.ceil(maxSeriesValue * 1.2) : undefined;

  const options: Record<string, any> = {
    color: seriesConfig.map((cfg, idx) => cfg.color || DEFAULT_BAR_COLORS[idx] || DEFAULT_BAR_COLORS[0]),
    grid: {
      top: 6,
      bottom: 26,
      left: 6,
      right: 36,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      confine: true,
      padding: 0,
      formatter: (tooltipParams: any) => {
        if (!Array.isArray(tooltipParams) || !tooltipParams.length) {
          return '';
        }
        const idx = tooltipParams[0].dataIndex;
        const dateTitle = sorted[idx]?.date
          ? formatStudioTooltipDate(sorted[idx].date, period, dateConfig)
          : '';

        const data = tooltipParams.map((tp: any) => {
          const v = tp?.value;
          const sIdx = tp?.seriesIndex ?? 0;
          const seriesCfg = seriesConfig[sIdx];
          const rule = seriesCfg?.formatter || formatRule;
          const valText =
            v === null || v === undefined || v === ''
              ? '-'
              : dataFormat({ value: v, formatRule: rule });
          const dotColor =
            tp?.color ||
            seriesCfg?.color ||
            DEFAULT_BAR_COLORS[sIdx] ||
            DEFAULT_BAR_COLORS[0];
          return {
            name: String(tp?.seriesName ?? ''),
            color: dotColor,
            value: valText,
          };
        });

        return getEchartTooltipString({
          className: 'studio-card-hor-bar-tooltip',
          table: {
            title: dateTitle,
            hideHeader: true,
            columns: [
              {
                key: 'name',
                header: '',
                showIcon: true,
                iconType: 'circular',
              },
              {
                key: 'value',
                header: '',
                align: 'right',
              },
            ],
            data,
          },
        });
      },
    },
    legend: {
      bottom: 0,
      itemWidth: 8,
      itemHeight: 8,
      icon: 'circle',
      textStyle: { fontSize: 10, color: '#7C7C7C' },
    },
    xAxis: {
      type: 'value',
      max: yAxisMax,
      splitLine: {
        show: true,
        lineStyle: { color: '#e5e5e5', type: 'dashed' },
      },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 10,
        color: '#7C7C7C',
        formatter: (value: number) => dataFormat({ value, formatRule: xAxisFormatRule }),
      },
    },
    yAxis: {
      type: 'category',
      data: yAxisData,
      axisLine: {
        show: true,
        lineStyle: { color: '#e5e5e5' },
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        interval: 0,
        fontSize: 10,
        color: '#7C7C7C',
        formatter: (value: string) => value,
      },
    },
    series,
  };

  const dataSource = sorted.map(item => {
    const row: Record<string, any> = {
      date: formatStudioTableDate(item?.date, dateConfig),
    };
    seriesConfig.forEach(cfg => {
      const v = item?.metrics?.[cfg.metricKey]?.value;
      row[cfg.metricKey] = v !== '' && v !== null && v !== undefined ? parseFloat(v) : '';
    });
    return row;
  });

  const nodata = !sorted.some(item =>
    seriesConfig.some(cfg => {
      const v = item?.metrics?.[cfg.metricKey]?.value;
      return v !== '' && v !== null && v !== undefined;
    }),
  );

  return { options, dataSource, nodata };
}

export const calculateDodPercent = (dod?: ICompareData): IDodResult => {
  if (!dod) {
    return {};
  }

  let percent: number | undefined;

  if (dod?.show_value) {
    percent = parseFloat(dod?.show_value);
  } else if (dod?.this_value && dod?.last_value) {
    const thisVal = parseFloat(dod?.this_value);
    const lastVal = parseFloat(dod?.last_value);
    if (lastVal !== 0) {
      percent = (thisVal - lastVal) / lastVal;
    }
  }

  if (typeof percent !== 'number' || isNaN(percent)) {
    return {};
  }

  return { percent };
};

export const formatPercent = (percent?: number): number | string => {
  if (typeof percent !== 'number' || isNaN(percent)) {
    return '';
  }
  return Math.round(percent * 100);
};
